import React, { useEffect } from 'react'
import classNames from 'classnames'
import { AuthenticatedHeader } from '../../components/Header/AuthenticatedHeader'
import { TopPanel } from '../../components/Header/TopPanel'
import gridClasses from '../../styles/grid.module.css'
import classes from './Cart.module.css'
import {
    ClassCartItem,
    CourseCartItem,
} from '../../components/CartItem/CartItem'
import { convertToFormatted, getImageURL } from '../../util/functions'
import { Button, ButtonIcon } from '../../components/Button/Button'
import { ReactComponent as ForwardIcon } from '../../assets/icons/buy-now-arrow-icon.svg'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { CourseCard } from '../../components/Cards/GeneralCard/Card'
import Footer from '../../components/Footer/Footer'
import { ReactComponent as RatingIcon } from '../../assets/icons/rating.svg'
import cartEmpty from '../../assets/images/empty-cart.svg'
import Modal from '../../components/Modal/Modal'
import successCurveTop from '../../assets/images/success-cart-curve-top.svg'
import successCurveBottom from '../../assets/images/success-cart-curve-bottom.svg'
import successIcon from '../../assets/images/cart-success-congratulations-icon.svg'
import { useDispatch, useSelector } from 'react-redux'
import { cartSelectors, CartItemTypes, cartActions } from './cartSlice'
import { userSelectors, userActions } from '../../redux/userSlice'
import { AccountTypes } from '../../util/constants'
import { removeItem, removeAllItems } from './cartApi'
import notification from '../../components/Notification/notification'
import { PaymentGatewayStatus, usePaymentGateway } from './usePaymentGateway'
import { PaymentModes } from './paymentLogic'
import { useAuthReminderModal } from '../../hooks/useAuthReminderModal'
import { recentlyViewedSelectors } from '../../redux/recentlyViewedSlice'
import placeholder from '../../assets/images/course-placeholder.svg'
import { wishListSelectors } from '../WishList/wishListSlice'
import {
    addToWishlist,
    isItemInWishlist,
    removeFromWishlist,
} from '../WishList/wishListApi'

const LoyaltyPointsInput = ({
    onUse,
    disabled = false,
    className,
    price,
    max,
    ...props
}) => {
    const [value, setValue] = useState('')
    const handleChange = event => {
        const { value } = event.target
        setValue(value)
    }
    const handleSubmit = event => {
        event.preventDefault()
        const intValue = parseInt(value)
        if (disabled || intValue > price || intValue > max) return
        if (typeof onUse === 'function') onUse(intValue)
    }

    return (
        <form
            className={classNames(classes.loyaltyInputWrapper, className)}
            onSubmit={handleSubmit}
            {...props}
        >
            <input
                placeholder="Use your Loyalty points"
                className={classes.loyaltyInput}
                value={value}
                onChange={handleChange}
                required
                pattern="[0-9]+"
                disabled={disabled}
            />
            <button
                type="submit"
                className={classes.loyaltyInputAction}
                disabled={disabled}
            >
                Use
            </button>
        </form>
    )
}

function renderCartItem(
    item,
    itemDetails,
    handleRemove,
    isWishlisted,
    onWishlist
) {
    if (item.type === CartItemTypes.Class) {
        return (
            <ClassCartItem
                key={item.cartItemId}
                date={itemDetails.startdate}
                time={itemDetails.starttime}
                title={itemDetails.title}
                slots={item.invites.length}
                price={convertToFormatted(itemDetails.amount, true, true)}
                onRemove={handleRemove}
                isWishlisted={isWishlisted}
                onWishlist={onWishlist}
            />
        )
    } else if (item.type === CartItemTypes.Course) {
        return (
            <CourseCartItem
                key={item.cartItemId}
                image={getImageURL(itemDetails.thumbnail)}
                title={itemDetails.title}
                price={convertToFormatted(itemDetails.amount, true, true)}
                onRemove={handleRemove}
                isWishlisted={isWishlisted}
                onWishlist={onWishlist}
            />
        )
    } else {
        if (process.env.NODE_ENV === 'development')
            console.warn(`Encountered unknown cart item type: ${item.type}`)
        return null
    }
}

export const CartPage = () => {
    const [success, setSuccess] = useState(false)
    const [purchasedItems, setPurchasedItems] = useState([])
    const [earnedPoints, setEarnedPoints] = useState(0)
    const dispatch = useDispatch()

    const { accounttype, loyalitypoint: ownedLoyaltyPoints } = useSelector(
        userSelectors.userDetails
    )

    const cartItems = useSelector(cartSelectors.cartItems)
    const cartItemDetails = useSelector(cartSelectors.cartItemDetails)
    const appliedLoyaltyPoints = useSelector(cartSelectors.appliedLoyaltyPoints)
    const totalAmount = useSelector(cartSelectors.totalAmount)
    const wishlistCourses = useSelector(wishListSelectors.courses)
    const wishListClasses = useSelector(wishListSelectors.classes)

    const isSubscriber =
        accounttype?.toLowerCase() === AccountTypes.Subscriber?.toLowerCase()

    const {
        validate,
        pay,
        processing,
        renderModal: renderPaymentProcessingModal,
        status,
    } = usePaymentGateway({
        onSuccessfulPayment({ customer, earnedPoints }) {
            setEarnedPoints(earnedPoints)
            setSuccess(true)
            dispatch(userActions.updateDetails({ userDetails: customer }))
            dispatch(removeAllItems())
        },
        mode: PaymentModes.Items,
    })

    const handleRemove = async cartItemId => {
        const [dismiss] = await notification.loading({
            title: 'Removing item from cart...',
        })
        const succesful = await dispatch(removeItem({ cartItemId }))
        dismiss()
        if (!succesful) {
            notification.failure({
                title: 'Failed to remove from cart',
                description: 'Please try again',
            })
        }
    }

    const handleCheckout = async () => {
        if (processing) return
        await validate()
    }

    useEffect(() => {
        if (status === PaymentGatewayStatus.ValidationSuccess) {
            setPurchasedItems(cartItems.map(i => cartItemDetails[i.cartItemId]))
            pay()
        }
    }, [status])

    const {
        renderModal: renderAuthReminderModal,
        conditionallyShowModal,
    } = useAuthReminderModal('/app/cart')

    const recentlyViewedCourses = useSelector(recentlyViewedSelectors.courses)

    const isInWishlist = (item, itemDetails) => {
        if (item.type === CartItemTypes.Course) {
            return isItemInWishlist(wishlistCourses, itemDetails.coursecode)
        } else if (item.type === CartItemTypes.Class) {
            return isItemInWishlist(wishListClasses, itemDetails.classid)
        }
        return false
    }

    const onToggleWishlist = (item, itemDetails) => {
        if (item.type === CartItemTypes.Course) {
            if (isItemInWishlist(wishlistCourses, itemDetails.coursecode)) {
                dispatch(
                    removeFromWishlist({
                        type: 'course',
                        itemId: itemDetails.coursecode,
                    })
                )
            } else {
                dispatch(
                    addToWishlist({
                        type: 'course',
                        itemId: itemDetails.coursecode,
                    })
                )
            }
        } else if (item.type === CartItemTypes.Class) {
            if (isItemInWishlist(wishListClasses, itemDetails.classid)) {
                dispatch(
                    removeFromWishlist({
                        itemId: itemDetails.classid,
                        type: 'class',
                    })
                )
            } else {
                dispatch(
                    addToWishlist({
                        itemId: itemDetails.classid,
                        type: 'class',
                    })
                )
            }
        }
    }

    return (
        <React.Fragment>
            <AuthenticatedHeader />
            <TopPanel title="My Cart" />
            <main
                className={classNames(
                    gridClasses.container,
                    classes.pageContent
                )}
            >
                <div>
                    <h5 className={classes.pageTitle}>Your item</h5>
                    <div className={classes.panes}>
                        <div className={classes.cartItems}>
                            {cartItems.length === 0 && (
                                <div
                                    className={`${classes.empty} ${classes.emptyActive}`}
                                >
                                    <img src={cartEmpty} alt="Empty cart" />
                                    <h3 className={classes.emptyText}>
                                        Your Cart is Empty
                                    </h3>
                                    <p className={classes.emptySubtext}>
                                        And it’s obviously not happy, purchase
                                        something to put a smile on it’s face.
                                    </p>
                                </div>
                            )}
                            {cartItems.map(cartItem => {
                                const itemDetail =
                                    cartItemDetails[cartItem.cartItemId]
                                return renderCartItem(
                                    cartItem,
                                    itemDetail,
                                    () => handleRemove(cartItem.cartItemId),
                                    isInWishlist(cartItem, itemDetail),
                                    () => onToggleWishlist(cartItem, itemDetail)
                                )
                            })}
                        </div>
                        <div className={classes.purchaseRegion}>
                            <div className={classes.priceBox}>
                                <h5 className={classes.priceBoxLabel}>
                                    Total Amount
                                </h5>
                                <h3 className={classes.priceBoxAmount}>
                                    {convertToFormatted(
                                        totalAmount,
                                        false,
                                        true
                                    )}
                                    {!!appliedLoyaltyPoints && (
                                        <span
                                            className={classes.originalAmount}
                                        >
                                            {convertToFormatted(
                                                totalAmount +
                                                    appliedLoyaltyPoints,
                                                true,
                                                true
                                            )}
                                        </span>
                                    )}
                                </h3>
                                <Button
                                    block
                                    className={classes.priceBoxAction}
                                    disabled={
                                        cartItems.length === 0 || processing
                                    }
                                    onClick={
                                        cartItems.length > 0
                                            ? conditionallyShowModal(
                                                  handleCheckout
                                              )
                                            : null
                                    }
                                >
                                    Buy Now
                                    <ButtonIcon icon={<ForwardIcon />} />
                                </Button>
                                {!!appliedLoyaltyPoints && (
                                    <p className={classes.pointCount}>
                                        {convertToFormatted(
                                            appliedLoyaltyPoints,
                                            false,
                                            true
                                        )}{' '}
                                        Loyalty points applied
                                    </p>
                                )}
                            </div>
                            <LoyaltyPointsInput
                                style={{ marginBottom: 10 }}
                                // disabled={!isSubscriber}
                                onUse={points =>
                                    dispatch(
                                        cartActions.applyLoyaltyPoints(points)
                                    )
                                }
                                price={totalAmount}
                                max={ownedLoyaltyPoints}
                            />
                            <p className={classes.loyaltyPointsDisplay}>
                                <span className={classes.loyaltyPointsIcon}>
                                    <RatingIcon />
                                </span>
                                You have{' '}
                                <span className={classes.emphasis}>
                                    {convertToFormatted(
                                        ownedLoyaltyPoints,
                                        false,
                                        true
                                    )}
                                </span>{' '}
                                Loyalty points
                            </p>
                            {/* {!isSubscriber && (
                                <p className={classes.loyaltyPointsMessage}>
                                    You currently cannot buy courses with
                                    loyalty points,{' '}
                                    <Link to="account/subscription">
                                        Upgrade your plan
                                    </Link>{' '}
                                    to enjoy this benefit
                                </p>
                            )} */}
                            {/* {isSubscriber && (
                                <p className={classes.loyaltyPointsDisplay}>
                                    <span className={classes.loyaltyPointsIcon}>
                                        <RatingIcon />
                                    </span>
                                    You have{' '}
                                    <span className={classes.emphasis}>
                                        {convertToFormatted(
                                            ownedLoyaltyPoints,
                                            false,
                                            true
                                        )}
                                    </span>{' '}
                                    Loyalty points
                                </p>
                            )} */}
                        </div>
                    </div>
                </div>
            </main>
            {recentlyViewedCourses.length > 0 && (
                <section className={classes.recentSection}>
                    <div
                        className={classNames(
                            gridClasses.container,
                            gridClasses.containerTransparent
                        )}
                    >
                        <h2 className={classes.recentSectionTitle}>
                            Recently Viewed Courses
                        </h2>
                        <div className={classes.recentCourses}>
                            {recentlyViewedCourses.map(course => (
                                <CourseCard
                                    className={classes.recentCourse}
                                    title={course.title}
                                    category={course.catName}
                                    price={course.price}
                                    attendees={course.enrolled}
                                    url={getImageURL(
                                        course.thumbnail,
                                        placeholder
                                    )}
                                    coursecode={course.coursecode}
                                    premium={!course.freeonsubcription}
                                />
                            ))}
                        </div>
                    </div>
                </section>
            )}
            <Modal
                open={success}
                className={classes.successModal}
                onRequestDismiss={_ => setSuccess(false)}
            >
                <img
                    src={successCurveTop}
                    alt="Curve"
                    className={classes.successModalCurveTop}
                />
                <img
                    src={successCurveBottom}
                    alt="Curve"
                    className={classes.successModalCurveBottom}
                />
                <img
                    src={successIcon}
                    alt="Curve"
                    className={classes.successModalIcon}
                />
                <h1 className={classes.successModalHeader}>Congratulations</h1>
                <p className={classes.successModalText}>
                    Your purchase was successful.
                </p>
                <p className={classes.successModalText}>
                    You have also earned{' '}
                    <span className={classes.successModalTextBold}>
                        <b>{earnedPoints}</b> Loyalty points
                    </span>
                </p>
                <div className={classes.successModalItems}>
                    {purchasedItems.map(itemDetail => (
                        <div className={classes.successModalItem}>
                            <p className={classes.successModalItemText}>
                                {itemDetail.title}
                            </p>
                        </div>
                    ))}
                </div>
                <Button to="/app/my-courses" bold>
                    My Courses
                </Button>
                <button
                    className={classes.successModalClose}
                    onClick={_ => setSuccess(false)}
                >
                    Back to cart
                </button>
            </Modal>
            {renderAuthReminderModal()}
            {renderPaymentProcessingModal()}
            <Footer />
        </React.Fragment>
    )
}
